/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  AfterViewInit,
  Directive,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {FormFieldAttribute} from '../../../../../models/FormCategory';
import {PassportService} from '../../../../../services/passport.service';
import {AbstractControl, FormControl} from '@angular/forms';
import {GenericFormBuilderService} from './components/generic-form-builder.service';
import {GeneralDataHandlingService} from '../../../../../services/general.data-handling.service';
import {debounceTime, Subject, takeUntil} from 'rxjs';
import {AppSettings} from '../../../../../models/Constants';
import {ToastService} from '@siemens/ix-angular';
import {TranslateService} from '@ngx-translate/core';
import {PassportDto, StatusStringEnum} from '../../../../../models/Passport';
import {NormalisedApiError} from '../../../../../models/errors/api-error';

@Directive()
export class BaseFormFieldDirective
  implements AfterViewInit, OnChanges, OnInit, OnDestroy
{
  @Input() attribute!: FormFieldAttribute;
  @Input() passportId!: string;
  @Input() passData?: unknown;
  @Input() fullValuePath!: string;
  @Input() saveFunction?: (
    value: string | number,
    attribute: FormFieldAttribute,
    control: AbstractControl,
    fullValuePath: string,
  ) => void = this.changeControlValue;

  readonly generalDataHandlingService = inject(GeneralDataHandlingService);
  readonly genericFormBuilder = inject(GenericFormBuilderService);
  readonly passportService = inject(PassportService);
  readonly toastService = inject(ToastService);
  readonly translate = inject(TranslateService);

  readonly destroy$ = new Subject<void>();

  isLoading = true;
  control!: FormControl;

  /** The most recent raw value emitted by `control.valueChanges` (before debounce). */
  private lastEmittedValue: unknown = undefined;
  /**
   * Whether a `valueChanges` emission has occurred (after subscriptions were
   * established) that has not yet been committed by the debounced handler.
   * Only emissions observed after subscription setup count — the initial,
   * programmatic `setComponentValue()` call in `ngOnInit`/`ngOnChanges` fires
   * before the subscriptions exist and is therefore never observed here.
   */
  private hasPendingChange = false;

  ngOnInit() {
    this.control = this.genericFormBuilder.createSimpleControl(this.attribute);
    this.setComponentValue(this.passData);
  }

  ngAfterViewInit(): void {
    this.control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.lastEmittedValue = value;
        this.hasPendingChange = true;
        if (this.passportService.onUnprocessableEntityError.value !== null) {
          this.passportService.onUnprocessableEntityError.next(null);
        }
      });
    this.control.valueChanges
      .pipe(
        debounceTime(AppSettings.INPUT_TO_REQUEST_WAIT_TIME_IN_MS),
        takeUntil(this.destroy$),
      )
      .subscribe(async (value) => {
        if (this.isCommittableValue(value)) {
          this.hasPendingChange = false;
          await this.onControlValueChanged(value, this.saveFunction);
        }
      });
    this.onControlValueInitialized();
    this.passportService.onUnprocessableEntityError
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => {
        if (error) {
          this.processUnprocessableEntityErrors(
            error,
            this.control,
            this.fullValuePath,
          );
        } else {
          this.clearErrors();
        }
      });
    // Deferred to a macrotask: setting `isLoading` synchronously here would
    // mutate a template-bound property after this component's own view has
    // already been checked in the same change-detection pass (ngAfterViewInit
    // runs after the view is rendered), triggering
    // ExpressionChangedAfterItHasBeenCheckedError (NG0100) in dev/test mode.
    setTimeout(() => {
      this.isLoading = false;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['passData']) {
      this.passData = changes['passData'].currentValue;
      this.setComponentValue(this.passData);
    }
  }

  /**
   * Flushes any pending, not-yet-persisted edit before the component's
   * subscriptions (and, with them, the debounced `valueChanges` pipeline)
   * are torn down.
   *
   * Without this, closing a host container right after typing — e.g.
   * clicking "OK" in the address modal within the debounce window, without
   * first blurring the field — silently discards the last edit: the
   * `debounceTime` timer is canceled by `takeUntil(this.destroy$)` before
   * it ever fires, so the change never reaches `passport.data`.
   */
  ngOnDestroy() {
    if (
      this.control &&
      this.hasPendingChange &&
      this.isCommittableValue(this.lastEmittedValue)
    ) {
      this.onControlValueChanged(this.lastEmittedValue, this.saveFunction);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Mirrors the guard previously inlined in the debounced `valueChanges`
   * subscription: number fields may legitimately emit `0`/falsy values,
   * everything else must not be `null`/`undefined` to be persisted.
   */
  private isCommittableValue(value: unknown): boolean {
    return (
      this.attribute?.type === 'number' ||
      (value !== null && value !== undefined)
    );
  }

  onControlValueInitialized(): void {
    // This method can be overridden in derived classes to handle initialization logic
  }

  protected clearErrors(): void {
    if (this.control.hasError('serverError')) {
      this.control.setErrors(null);
    }
  }

  async onControlValueChanged(
    value: unknown,
    fn = this.changeControlValue,
  ): Promise<void> {
    fn.call(this, value, this.attribute, this.control, this.fullValuePath);
  }

  changeControlValue(
    value: unknown,
    _attribute: FormFieldAttribute,
    _control: AbstractControl,
    fullValuePath: string,
  ) {
    const passport =
      this.passportService.passes()[this.passportId] ??
      ({
        data: {},
        metadata: {status: {status: StatusStringEnum.DRAFT}},
      } as unknown as PassportDto);
    const checkedValue = this.checkValue(value);
    this.generalDataHandlingService.setValueByPath(
      passport.data,
      fullValuePath,
      checkedValue,
    );
    this.passportService
      .update(this.passportId, passport, undefined)
      .catch(async (error) => {
        const validationErrorMessage = this.translate.instant(
          'battery_pass.edit.errors.validation_error',
        );
        const generalErrorMessage = this.translate.instant(
          'battery_pass.edit.errors.unknown_error',
        );
        const title = this.translate.instant(
          'battery_pass.edit.errors.validation_error_title',
        );
        await this.toastService.show({
          title,
          message:
            error.status === 0 ? generalErrorMessage : validationErrorMessage,
          type: 'error',
        });
      });
  }

  processUnprocessableEntityErrors(
    normalisedError: NormalisedApiError,
    control: AbstractControl,
    fullValuePath: string,
  ) {
    for (const entry of normalisedError.all) {
      const code = entry.code ?? '';
      const jsonPathField = this.normalizeFieldPath(
        entry.source?.pointer ?? '',
      );
      const isMissingRequired = code === 'MISSING_REQUIRED_PROPERTY';
      const pointsToExactField = jsonPathField === fullValuePath;

      const conditionApplies =
        !isMissingRequired || pointsToExactField || this.isAttributeRequired();

      if (
        fullValuePath.startsWith(jsonPathField) &&
        conditionApplies &&
        !control.errors
      ) {
        control.setErrors({serverError: code});
      }
    }
  }

  /**
   * Normalizes a field path or RFC 6901 JSON Pointer to Angular dot-bracket
   * notation so it can be compared against `fullValuePath`.
   *
   * Handles two input formats:
   * - **RFC 6901 / slash-separated** (legacy backend format, ADR-003):
   *   e.g. `/generalProductInformation/manufacturerInformation` or
   *   `/labeling/labels/0`
   * - **Dot-separated** (new backend format, BL-4977):
   *   e.g. `generalProductInformation.manufacturerInformation.trademark` or
   *   `labeling.labels.0.labelingSymbol`
   *
   * In both cases numeric path segments are converted to bracket notation so
   * the result matches the `fullValuePath` used by form-field components:
   * e.g. `labeling.labels[0].labelingSymbol`.
   *
   * @param {string} path - The raw path string from `source.pointer` of a
   *   `NormalisedApiError` entry.
   * @returns {string} The normalized dot-bracket path string.
   */
  protected normalizeFieldPath(path: string): string {
    // Strip RFC 6901 leading slash if present
    let normalized = path.startsWith('/') ? path.slice(1) : path;
    // Array index mid-path (slash-separated RFC 6901): /0/ → [0].
    normalized = normalized.replace(/\/(\d+)\//g, '[$1].');
    // Array index at end (slash-separated RFC 6901): /0 → [0]
    normalized = normalized.replace(/\/(\d+)$/, '[$1]');
    // Remaining slash path separators → dots
    normalized = normalized.replace(/\//g, '.');
    // Array index mid-path (dot-separated, new backend format): .0. → [0].
    normalized = normalized.replace(/\.(\d+)\./g, '[$1].');
    // Array index at end (dot-separated, new backend format): .0 → [0]
    normalized = normalized.replace(/\.(\d+)$/, '[$1]');
    return normalized;
  }

  /**
   * Determines whether the current attribute is required according to the JSON schema.
   *
   * Reads the schema synchronously from the passport service cache (populated when the
   * form builder loads the schema) and evaluates all `allOf` conditionals against the
   * live passport data so category-specific required fields are resolved correctly.
   * Falls back to `true` when the schema is not yet cached.
   *
   * @returns {boolean} true when the attribute's path appears in the schema's required fields
   */
  protected isAttributeRequired(): boolean {
    return this.attribute.isRequired === true;
  }

  checkValue(value: unknown) {
    if (typeof value === 'string') {
      return value.length !== 0 ? value.trim() : null;
    } else if (Array.isArray(value)) {
      return value.length !== 0 ? value : null;
    } else if (value === undefined) {
      // Some controls (e.g. `ix-number-input` with `allowEmptyValueChange`)
      // emit `undefined` when cleared instead of `null`. `undefined` values
      // are stripped by `JSON.stringify` when the passport is sent to the
      // backend, so the field would silently keep its previous value
      // instead of being reset. Normalizing to `null` ensures the reset is
      // actually persisted.
      return null;
    } else {
      return value;
    }
  }

  createInvalidText(control: AbstractControl): string {
    if (control?.errors?.['minlength']) {
      return this.translate.instant('battery_pass.edit.errors.min_chars', {
        min: this.attribute.min,
      });
    } else if (control?.errors?.['serverError']) {
      const code = control.errors['serverError'];
      return this.translate.instant(`battery_pass.edit.errors.${code}`);
    } else if (control?.errors?.['max']) {
      return this.translate.instant('battery_pass.edit.errors.max', {
        max: this.attribute.max,
      });
    } else if (control?.errors?.['min']) {
      return this.translate.instant('battery_pass.edit.errors.min', {
        min: this.attribute.min,
      });
    } else if (control?.errors?.['invalidStep']) {
      return this.translate.instant('battery_pass.edit.errors.invalid_digits', {
        digits: this.attribute.digits,
      });
    } else if (control?.errors?.['email']) {
      return this.translate.instant('battery_pass.edit.errors.invalid_email');
    } else if (control?.errors?.['invalidUrl']) {
      return this.translate.instant('battery_pass.edit.errors.invalid_uri');
    } else {
      return this.createCustomInvalidText(control);
    }
  }

  createCustomInvalidText(_control: AbstractControl): string {
    // This method can be overridden in derived classes to create custom invalid text
    return '';
  }

  setComponentValue(value: unknown): void {
    if (!this.control || value === undefined || value === null) {
      return;
    }
    const actualValue = this.control.getRawValue();
    if (!actualValue) {
      this.control.setValue(value, {emitEvent: false});
    }
  }

  getData(valuePath: string): unknown | undefined {
    if (!this.passData) {
      return undefined;
    }
    return (
      this.generalDataHandlingService.getValueByPath(
        this.passData,
        valuePath,
      ) ?? ''
    );
  }
}
